import React, { useEffect, useState } from 'react';
import { Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';

interface UploadProgressToastProps {
  isUploading: boolean;
  totalCount: number;
  completedCount: number;
  failedCount: number;
  mediaType?: 'video' | 'music' | 'photo' | 'mixed';
  className?: string;
  /**
   * Individual file progress percentages (0-100) for smooth aggregate calculation
   * Key: file ID, Value: progress percentage
   */
  individualProgress?: Record<string, number>;
  /**
   * Callback when toast is dismissed after upload completion
   * Used to reset the upload counter in the context
   */
  onDismiss?: () => void;
}

/**
 * Persistent upload progress notification
 *
 * Shows live upload progress with:
 * - Current progress count (e.g., "Uploading videos: 3 of 10")
 * - Progress bar
 * - Warning to not close the tab
 * - Success/error states
 *
 * Positioned bottom-right on desktop, bottom-center on mobile
 * Uses glassmorphism styling to match the design system
 */
export function UploadProgressToast({
  isUploading,
  totalCount,
  completedCount,
  failedCount,
  mediaType = 'mixed',
  className,
  individualProgress = {},
  onDismiss,
}: UploadProgressToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Swipe gesture tracking
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchOffset, setTouchOffset] = useState(0);

  // Show toast when uploading starts
  useEffect(() => {
    if (isUploading) {
      setIsVisible(true);
      setShowSuccess(false);
      setIsDismissed(false);
      setIsMinimized(false);
    }
  }, [isUploading]);

  // Handle upload completion
  useEffect(() => {
    if (!isUploading && isVisible && completedCount > 0) {
      // Show success state briefly
      setShowSuccess(true);

      // Auto-hide after 3 seconds
      const timer = setTimeout(() => {
        setIsVisible(false);
        setShowSuccess(false);
        // Reset counter when toast dismisses after completion
        onDismiss?.();
      }, 3000);

      return () => clearTimeout(timer);
    }

    // All files were discarded (none completed) - dismiss immediately
    if (!isUploading && isVisible && totalCount === 0 && completedCount === 0) {
      setIsVisible(false);
      onDismiss?.();
    }
  }, [isUploading, isVisible, completedCount, totalCount, onDismiss]);

  // Swipe gesture handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const deltaX = e.touches[0].clientX - touchStart.x;
    const deltaY = Math.abs(e.touches[0].clientY - touchStart.y);

    // Track horizontal swipe in both directions, ignore if vertical movement is too large
    if (deltaY < 50) {
      setTouchOffset(deltaX);
    }
  };

  const handleTouchEnd = () => {
    if (touchOffset > 100) {
      // Swipe right - collapse
      setIsMinimized(true);
    } else if (touchOffset < -100) {
      // Swipe left - expand
      setIsMinimized(false);
    }
    setTouchOffset(0);
    setTouchStart(null);
  };

  // Handle click to minimize (when expanded) or maximize (when minimized)
  const handleClick = () => {
    if (showSuccess) return; // Don't toggle when showing success
    setIsMinimized(!isMinimized);
  };

  // Don't render if not visible
  if (!isVisible) return null;

  const pendingCount = totalCount - completedCount - failedCount;

  /**
   * Calculate smooth aggregate progress
   *
   * Simple formula:
   * (completedCount * 100 + sum of individualProgress) / totalCount
   *
   * BUT: individualProgress already includes completed files at 100%,
   * so we need to avoid double-counting:
   * - Only use individualProgress values < 100 (in-progress files)
   * - Add completedCount * 100 for finished files
   */
  const calculateSmoothProgress = (): number => {
    if (totalCount === 0) return 0;

    // Completed files: 100% each
    const completedProgress = completedCount * 100;

    // In-progress files: sum only values < 100 (exclude completed)
    const inProgressTotal = Object.values(individualProgress)
      .filter(progress => progress < 100)
      .reduce((sum, progress) => sum + progress, 0);

    // Total: completed + in-progress
    const totalProgress = (completedProgress + inProgressTotal) / totalCount;

    // Cap at 99% if over 99 but not truly 100%
    if (totalProgress >= 100) return 100;
    if (totalProgress > 99) return 99;
    return Math.max(0, totalProgress);
  };

  const progress = calculateSmoothProgress();
  const hasErrors = failedCount > 0;

  // Determine media type label (simplified to just use "file/files")
  const getMediaLabel = () => {
    return totalCount === 1 ? 'file' : 'files';
  };

  return (
    <div
      className={cn(
        // Sizing - shrink to small box when minimized, full width when expanded
        // ALWAYS keep ml-auto so position doesn't animate
        isMinimized ? 'w-16 h-16' : 'w-full h-auto',
        'ml-auto',
        // Glassmorphism styling
        'bg-white/10 backdrop-blur-md border border-white/20',
        'rounded-lg shadow-lg',
        // Allow interaction with this toast
        'pointer-events-auto',
        // Clip overflowing content during animation
        'overflow-hidden',
        // Animation
        isDismissed ? 'animate-out slide-out-to-right-full duration-200' : 'animate-in slide-in-from-bottom-5 duration-300',
        // Smooth transitions for width/height only (not margin!)
        'transition-[width,height] duration-200',
        // Transform origin - expand/collapse from the right side
        'origin-right',
        // Cursor pointer (entire card is clickable)
        !showSuccess && 'cursor-pointer hover:bg-white/20',
        className
      )}
      style={{
        transform: touchOffset !== 0 ? `translateX(${touchOffset}px)` : undefined
      }}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      aria-label={isMinimized ? "Click to expand upload progress" : "Click to minimize upload progress"}
    >
      <div className={cn(isMinimized ? "p-0 flex items-center justify-center h-full w-full" : "p-3 space-y-2")}>
        {/* Minimized view - just percentage in small box */}
        {isMinimized && !showSuccess ? (
          <div className="text-sm font-bold text-white">
            {Math.round(progress)}%
          </div>
        ) : (
          /* Expanded view - full details */
          <>
            {/* Header */}
            <div className="flex items-start gap-2.5">
              <div className="flex-shrink-0 mt-0.5">
                {showSuccess ? (
                  <CheckCircle2 className="w-4 h-4 text-green-300" />
                ) : hasErrors ? (
                  <AlertCircle className="w-4 h-4 text-yellow-300" />
                ) : (
                  <Upload className="w-4 h-4 text-white animate-pulse" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                {/* Title with percentage */}
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-semibold text-white">
                    {showSuccess ? (
                      hasErrors ? (
                        <>Upload completed with {failedCount} {failedCount === 1 ? 'error' : 'errors'}</>
                      ) : (
                        <>Upload complete</>
                      )
                    ) : (
                      <>Uploading {getMediaLabel()}: {completedCount} of {totalCount}</>
                    )}
                  </div>

                  {/* Percentage - far right, bigger, white */}
                  {!showSuccess && (
                    <div className="text-base font-bold text-white flex-shrink-0">
                      {Math.round(progress)}%
                    </div>
                  )}
                </div>

                {/* Warning message - above progress bar */}
                {!showSuccess && (
                  <div className="mt-1.5 flex items-center gap-1 text-[10px] text-yellow-300">
                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                    <span>Please do not close this window</span>
                  </div>
                )}

                {/* Progress bar - only show during upload */}
                {!showSuccess && (
                  <div className="mt-1">
                    <Progress
                      value={progress}
                      className="h-1.5 bg-white/20"
                    />
                  </div>
                )}

                {/* Success message */}
                {showSuccess && !hasErrors && (
                  <div className="mt-0.5 text-[10px] text-white/70">
                    Successfully uploaded {completedCount} {getMediaLabel()}
                  </div>
                )}

                {/* Error details */}
                {showSuccess && hasErrors && (
                  <div className="mt-0.5 text-[10px] text-white/70">
                    {completedCount} succeeded, {failedCount} failed
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
